import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import { FieldValues } from 'features/libra/ontology/ontologyFieldTypes'
import { ComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import reduce from 'lodash/reduce'

import { getComputeFunction } from './computeFunctionRegistry'

export interface ComputedSectionResult {
  computedResult: boolean | undefined
  isOverridden: boolean
  overrideValue: boolean | undefined
  finalResult: boolean | undefined
  overrideJustification: string | undefined
}
export function resolveParameters(
  config: ComputedSection,
  fieldValues: FieldValues
): Record<string, unknown> {
  return reduce(
    config.functionSpec.parameters,
    (acc, paramSpec) => {
      const value = unwrapFieldValue(fieldValues[paramSpec.field])

      if (value !== undefined && value !== null) {
        acc[paramSpec.field] = value
      } else if (paramSpec.defaultValue !== undefined) {
        acc[paramSpec.field] = paramSpec.defaultValue
      } else {
        acc[paramSpec.field] =
          'type' in paramSpec && paramSpec.type === 'tri-state' ? 'low' : false
      }

      return acc
    },
    {} as Record<string, unknown>
  )
}
export function evaluateComputedSection(
  config: ComputedSection,
  fieldValues: FieldValues,
  context?: Record<string, unknown>
): boolean | undefined {
  const fn = getComputeFunction(config.function)
  if (!fn) return undefined

  const params = resolveParameters(config, fieldValues)
  const computedContext = { functionSpec: config.functionSpec, ...context }

  return fn(params, computedContext)
}
export function evaluateComputedSectionWithOverride(
  config: ComputedSection,
  fieldValues: FieldValues,
  context?: Record<string, unknown>
): ComputedSectionResult {
  const computedResult = evaluateComputedSection(config, fieldValues, context)

  const rawOverrideValue =
    config.allowOverride && config.overrideField
      ? unwrapFieldValue<boolean>(fieldValues[config.overrideField])
      : undefined
  const overrideValue =
    typeof rawOverrideValue === 'boolean' ? rawOverrideValue : undefined

  const rawOverrideJustification =
    config.allowOverride && config.overrideJustificationField
      ? unwrapFieldValue<string>(fieldValues[config.overrideJustificationField])
      : undefined
  const overrideJustification =
    typeof rawOverrideJustification === 'string'
      ? rawOverrideJustification
      : undefined

  const isOverridden = overrideValue !== undefined
  const finalResult = isOverridden ? overrideValue : computedResult

  return {
    computedResult,
    isOverridden,
    overrideValue,
    finalResult,
    overrideJustification,
  }
}
